import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { useTheme } from '@mui/styles';
import clsx from 'clsx';

import {
  AppBar,
  Box,
  ClickAwayListener,
  Divider,
  ListItemText,
  MenuItem,
  Toolbar,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Home, Menu } from '@mui/icons-material';

import ToggleMobileSearchIconContainer from 'generic/containers/ToggleMobileSearchIconContainer';
import { utilisateurPropType } from 'generic/core/qes/proptypes';
import AccountMenu from 'generic/components/ui/AccountMenu';
import DropdownMenu from 'generic/components/ui/DropdownMenu';
import { overrideRessource } from 'generic/utils/utils';
import { isHeaderBackgroundColorDark } from 'generic/utils/colorUtils';
import HeaderSearchWrapper from 'generic/components/ui/HeaderSearchWrapper';
import { useSelector } from 'react-redux';
import HeaderMenuItem from 'generic/components/ui/HeaderMenuItem';

const logoWhite = overrideRessource('img/header-logo.svg', true);
const logoBlack = overrideRessource('img/header-logo-black.svg', true);

const LinksMenu = ({
  webTrackingAccess, mobile, hasWatches, hasCarts, hasCollections, hasNewsletters,
}) => {
  const defaultBaseId = useSelector((state) => state.config.settings.baseInterne_368);
  const { t } = useTranslation();
  const location = useLocation();

  const Item = mobile ? MenuItem : HeaderMenuItem;
  return (
    <Fragment>
      {hasCollections && (
        <Tooltip title={t('header.collections')}>
          <Item
            component={NavLink}
            to="/collections"
            selected={location.pathname.startsWith('/collections')}
          >
            <Home />
          </Item>
        </Tooltip>
      )}
      {webTrackingAccess && (
        <Item
          component={NavLink}
          to="/anr/folders"
          selected={location.pathname.startsWith('/anr/folders')}
        >
          <ListItemText>{t('header.web_tracking')}</ListItemText>
        </Item>
      )}

      {hasCarts && (
        <Item
          component={NavLink}
          to="/carts"
          selected={location.pathname.startsWith('/carts')}
        >
          <ListItemText>{t('header.carts')}</ListItemText>
        </Item>
      )}

      {hasNewsletters && (
        <Item
          component={NavLink}
          to="/newsletters"
          selected={location.pathname.startsWith('/newsletters')}
        >
          <ListItemText>{t('header.newsletters')}</ListItemText>
        </Item>
      )}

      {hasWatches && (
        <Item
          component={NavLink}
          to="/alerts"
          selected={location.pathname.startsWith('/alerts')}
        >
          <ListItemText>{t('header.alerts')}</ListItemText>
        </Item>
      )}

      {mobile && (
        <Fragment>
          <Divider />
          <Item
            component={NavLink}
            to={`/search/dashboard/${defaultBaseId}${location?.search || ''}`}
            selected={location.pathname.startsWith('/search/dashboard')}
          >
            <ListItemText>{t('header.dashboard')}</ListItemText>
          </Item>
          <Item
            component={NavLink}
            to={`/search/results/${defaultBaseId}${location?.search || ''}`}
            selected={location.pathname.startsWith('/search/results')}
          >
            <ListItemText>{t('header.results')}</ListItemText>
          </Item>
        </Fragment>
      )}
    </Fragment>
  );
};

LinksMenu.propTypes = {
  webTrackingAccess: PropTypes.bool,
  mobile: PropTypes.bool,
  hasCollections: PropTypes.bool,
  hasWatches: PropTypes.bool,
  hasCarts: PropTypes.bool,
  hasNewsletters: PropTypes.bool,
};

LinksMenu.defaultProps = {
  webTrackingAccess: false,
  mobile: false,
  hasCollections: false,
  hasWatches: false,
  hasCarts: false,
  hasNewsletters: false,
};

const Header = ({
  user,
  hasActiveCollection,
  hasCarts,
  hasCollections,
  hasNewsletters,
  hasWatches,
}) => {
  const [isSearchInputVisible, setIsSearchInputVisible] = useState(true);
  const theme = useTheme();
  const smallerThanLarge = useMediaQuery(theme.breakpoints.down('lg'));

  useEffect(() => {
    setIsSearchInputVisible(!smallerThanLarge);
  }, [smallerThanLarge]);

  const forceHideMenusForMobile = smallerThanLarge && isSearchInputVisible;
  const forceShowMenusForMobile = smallerThanLarge && !isSearchInputVisible;
  const toolbarSx = smallerThanLarge ? { px: '10px' } : {};
  const activeLogo = isHeaderBackgroundColorDark(theme) ? logoWhite : logoBlack;

  return (
    <ClickAwayListener
      onClickAway={() => (forceHideMenusForMobile ? setIsSearchInputVisible(false) : _.noop)}
      mouseEvent="onMouseDown"
    >
      <Box width="100%">
        <AppBar
          elevation={0}
          position="static"
          color="header"
          enableColorOnDark
          sx={{ ...(theme.palette.mode === 'dark' && { borderBottom: '1px solid', borderColor: 'divider' }) }}
        >
          <Toolbar
            className={clsx('displayFlex', 'justifyContentSpaceBetween')}
            sx={toolbarSx}
          >
            <Fragment>
              <Box
                display="flex"
                alignItems="center"
                gap={1}
              >
                <img
                  src={activeLogo}
                  alt="Qwam Logo"
                  style={{
                    display: forceHideMenusForMobile ? 'none' : 'block',
                    marginRight: '24px',
                    ...theme?.custom?.logo || '',
                  }}
                />
                { hasActiveCollection && (
                  <Fragment>
                    <DropdownMenu
                      btnChildren={<Menu />}
                      btnProps={{
                        sx: {
                          display: forceShowMenusForMobile ? 'flex' : 'none',
                          backgroundColor: isHeaderBackgroundColorDark(theme) ? 'unset' : 'rgba(0, 0, 0, 0.1)',
                          padding: '8px',
                          color: isHeaderBackgroundColorDark(theme) ? '#ffffff' : '#000000',
                          '&:hover': {
                            backgroundColor: isHeaderBackgroundColorDark(theme)
                              ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
                          },
                        },
                      }}
                    >
                      <LinksMenu
                        mobile
                        webTrackingAccess={user.accesAnR}
                        hasCarts={hasCarts}
                        hasNewsletters={hasNewsletters}
                        hasWatches={hasWatches}
                      />
                    </DropdownMenu>
                    {!smallerThanLarge && (
                      <LinksMenu
                        webTrackingAccess={user.accesAnR}
                        hasCarts={hasCarts}
                        hasNewsletters={hasNewsletters}
                        hasWatches={hasWatches}
                        hasCollections={hasCollections}
                      />
                    )}
                  </Fragment>
                )}
              </Box>
              {hasActiveCollection && (
                <HeaderSearchWrapper
                  showSearchInput={isSearchInputVisible}
                  headerBackgroundColorIsDark={isHeaderBackgroundColorDark(theme)}
                />
              )}
              <Box display="flex" alignItems="center">
                {hasActiveCollection && (
                  <ToggleMobileSearchIconContainer
                    handleClick={() => setIsSearchInputVisible(true)}
                    isVisible={forceShowMenusForMobile}
                    headerBackgroundColorIsDark={isHeaderBackgroundColorDark(theme)}
                  />
                )}
                <Box
                  sx={{
                    display: forceHideMenusForMobile ? 'none' : 'inline-block',
                  }}
                >
                  { user && (
                    <AccountMenu
                      user={user}
                      hasActiveCollection={hasActiveCollection}
                      hasCarts={hasCarts}
                      hasCollections={hasCollections}
                      hasWatches={hasWatches}
                      webTrackingAccess={user.accesAnR}
                      avatarSx={
                        { boxShadow: `0 0 4px 0px ${isHeaderBackgroundColorDark(theme) ? '#ffffff' : '#c5c5c5'}` }
                      }
                    />
                  )}
                </Box>
              </Box>
            </Fragment>
          </Toolbar>
        </AppBar>
      </Box>
    </ClickAwayListener>
  );
};

Header.propTypes = {
  user: utilisateurPropType,
  hasActiveCollection: PropTypes.bool,
  hasCarts: PropTypes.bool,
  hasCollections: PropTypes.bool,
  hasNewsletters: PropTypes.bool,
  hasWatches: PropTypes.bool,
};

Header.defaultProps = {
  user: undefined,
  hasActiveCollection: false,
  hasCarts: false,
  hasCollections: false,
  hasNewsletters: false,
  hasWatches: false,
};

export default Header;
